import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Search, Mail, Phone, Building, Calendar, RefreshCw, FileDown } from 'lucide-react';
import { format } from 'date-fns';

interface Lead {
  id: string;
  email: string;
  name: string | null;
  phone: string | null;
  company: string | null;
  lead_source: string;
  lead_type: string;
  status: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
  totalActivities: number;
  activityBreakdown: {
    downloads: number;
    assessments: number;
    contactForms: number;
  };
  downloadDetails?: Array<{
    resourceName: string;
    resourceType: string;
    downloadedAt: string;
  }>;
}

interface LeadsManagementProps {
  leads: Lead[];
  loading: boolean;
  onRefresh: () => void;
  onLeadClick?: (lead: Lead) => void;
}

export const LeadsManagement = ({ leads, loading, onRefresh, onLeadClick }: LeadsManagementProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [showDownloadsDialog, setShowDownloadsDialog] = useState(false);

  const filteredLeads = leads.filter((lead) =>
    lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (lead.name && lead.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (lead.company && lead.company.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
        return 'default';
      case 'contacted':
        return 'secondary';
      case 'qualified':
        return 'outline';
      case 'converted':
        return 'default';
      default:
        return 'secondary';
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Lead Management
            </CardTitle>
            <CardDescription>
              View and track leads from contact forms and assessments
            </CardDescription>
          </div>
          <Button onClick={onRefresh} variant="outline" size="sm" disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by email, name, or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Contact</TableHead>
                <TableHead>Activity Summary</TableHead>
                <TableHead>Latest Activity</TableHead>
                <TableHead>First Seen</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLeads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground">
                    {searchTerm ? 'No matching leads found' : 'No leads yet'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredLeads.map((lead) => (
                  <TableRow 
                    key={lead.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => onLeadClick?.(lead)}
                  >
                    <TableCell>
                      <div className="space-y-1">
                        {lead.name && (
                          <div className="font-medium">{lead.name}</div>
                        )}
                        <div className={`flex items-center gap-1 text-sm ${!lead.name ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                          <Mail className="w-3 h-3" />
                          {lead.email}
                        </div>
                        {lead.phone && (
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Phone className="w-3 h-3" />
                            {lead.phone}
                          </div>
                        )}
                        {lead.company && (
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Building className="w-3 h-3" />
                            {lead.company}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {lead.activityBreakdown.downloads > 0 ? (
                        <Badge 
                          variant="default" 
                          className="cursor-pointer hover:bg-primary/80 transition-colors"
                          onClick={() => {
                            setSelectedLead(lead);
                            setShowDownloadsDialog(true);
                          }}
                        >
                          <FileDown className="w-3 h-3 mr-1" />
                          {lead.activityBreakdown.downloads} download{lead.activityBreakdown.downloads > 1 ? 's' : ''}
                        </Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">No downloads</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Calendar className="w-3 h-3" />
                        {format(new Date(lead.updated_at), 'MMM d, yyyy')}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Calendar className="w-3 h-3" />
                        {format(new Date(lead.created_at), 'MMM d, yyyy')}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusColor(lead.status)}>{lead.status}</Badge>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="text-sm text-muted-foreground">
          Showing {filteredLeads.length} of {leads.length} leads
        </div>
      </CardContent>

      <Dialog open={showDownloadsDialog} onOpenChange={setShowDownloadsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Downloaded Resources</DialogTitle>
            <DialogDescription>
              {selectedLead?.name || selectedLead?.email}
            </DialogDescription>
          </DialogHeader>
          
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Resource Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Downloaded</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {selectedLead?.downloadDetails && selectedLead.downloadDetails.length > 0 ? (
                  selectedLead.downloadDetails
                    .sort((a, b) => new Date(b.downloadedAt).getTime() - new Date(a.downloadedAt).getTime())
                    .map((download, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <FileDown className="w-4 h-4 text-primary" />
                            {download.resourceName}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">
                            {download.resourceType}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Calendar className="w-3 h-3" />
                            {format(new Date(download.downloadedAt), 'MMM d, yyyy HH:mm')}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-muted-foreground">
                      No downloads yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
